import api from './axiosInstance';

/**
 * Post-start Change Approval Service.
 *
 * Mirrors the backend `/approvals/project/...` endpoints for the post-start
 * change-approval flow. When a project is already running and a requester
 * edits an asset / checkpoint / equipment, that document is flagged with
 * `postStartChange.hasPendingChanges = true` and is skipped by the unified
 * task generator until a head/approver explicitly resolves it.
 *
 * The screens used by approvers (project approval dashboard, etc.) consume
 * these endpoints to list and resolve pending changes.
 */

export type PostStartChangeResource = 'asset' | 'checkpoint' | 'equipment';

export interface PostStartChangeRow {
  resource: PostStartChangeResource;
  _id: string;
  name?: string;
  changedAt?: string | null;
  changedBy?:
    | string
    | {
        _id: string;
        name?: string;
        email?: string;
      }
    | null;
  changedFields?: string[];
  rejectionReason?: string;
  parent?: { _id?: string; name?: string };
}

export interface ProjectPendingChangesData {
  projectId: string;
  total: number;
  assets: PostStartChangeRow[];
  checkpoints: PostStartChangeRow[];
  equipment: PostStartChangeRow[];
}

export interface ProjectPendingChangesResponse {
  success: boolean;
  message: string;
  data: ProjectPendingChangesData;
}

export interface PostStartChangeMutationResponse {
  success: boolean;
  message: string;
  data: any;
}

const RESOURCE_PATH: Record<PostStartChangeResource, string> = {
  asset: 'asset',
  checkpoint: 'checkpoint',
  equipment: 'equipment',
};

/**
 * Fetch the list of post-start changes that are still pending head approval
 * for a given project. Used by the approver review screen.
 */
export const getProjectPendingChanges = async (
  projectId: string
): Promise<ProjectPendingChangesResponse> => {
  if (!projectId) {
    throw new Error('projectId is required');
  }
  const response = await api.get(
    `/approvals/project/${projectId}/pending-changes`
  );
  return response.data;
};

/**
 * Approve a single post-start change (asset / checkpoint / equipment).
 * Once approved, the entity is re-eligible for task generation on the next run.
 */
export const approvePostStartChange = async (
  resource: PostStartChangeResource,
  resourceId: string,
  comments?: string
): Promise<PostStartChangeMutationResponse> => {
  if (!resourceId) {
    throw new Error('resourceId is required');
  }
  const response = await api.post(
    `/approvals/project/${RESOURCE_PATH[resource]}/${resourceId}/approve-changes`,
    { comments }
  );
  return response.data;
};

/**
 * Reject a single post-start change. The entity stays excluded from task
 * generation until the requester edits again or an approver approves.
 * The reason is required by the backend.
 */
export const rejectPostStartChange = async (
  resource: PostStartChangeResource,
  resourceId: string,
  reason: string
): Promise<PostStartChangeMutationResponse> => {
  if (!resourceId) {
    throw new Error('resourceId is required');
  }
  if (!reason || !reason.trim()) {
    throw new Error('Rejection reason is required');
  }
  const response = await api.post(
    `/approvals/project/${RESOURCE_PATH[resource]}/${resourceId}/reject-changes`,
    { reason }
  );
  return response.data;
};

/**
 * Bulk-approve every pending post-start change in the given project.
 * Useful when an approver has already reviewed individually and wants to
 * release the whole batch at once.
 */
export const approveAllProjectPendingChanges = async (
  projectId: string,
  comments?: string
): Promise<PostStartChangeMutationResponse> => {
  if (!projectId) {
    throw new Error('projectId is required');
  }
  const response = await api.post(
    `/approvals/project/${projectId}/approve-all-pending-changes`,
    { comments }
  );
  return response.data;
};

/**
 * Requester-side action: submit the project's accumulated post-start changes
 * for head re-approval. Used when the project is already approved and the
 * manager has edited assets / checkpoints / added equipment afterward — the
 * existing `Send for Review` / `Resubmit` buttons don't cover this state,
 * because they reject already-approved projects.
 *
 * Does NOT touch `Project.approvalStatus`. Only creates an Approval record so
 * the change batch shows up in the head's pending queue.
 */
export const submitProjectPendingChanges = async (
  projectId: string,
  comments?: string
): Promise<PostStartChangeMutationResponse> => {
  if (!projectId) {
    throw new Error('projectId is required');
  }
  const response = await api.post(
    `/approvals/project/${projectId}/submit-pending-changes`,
    { comments }
  );
  return response.data;
};

export default {
  getProjectPendingChanges,
  approvePostStartChange,
  rejectPostStartChange,
  approveAllProjectPendingChanges,
  submitProjectPendingChanges,
};
